import { useCallback, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { CapacityLimitsFields, CapacityPresetKey, GuardedRpcRequest, GuardedRpcResult, RpcResult } from '@brunel/shared';
import { prepareMutation, sendWithRetry, type BrowserRpcName, type RpcRequestBuilder, type RpcTransport, type RpcTransportContext } from './rpc';
import type { LimitsFields } from './capacity';
import type { WorkerChoice } from './data';

export function transportFor(client: SupabaseClient): RpcTransport {
  return { rpc: (name, args) => client.rpc(name, args) as unknown as RpcRequestBuilder };
}

const MESSAGES: Record<string, string> = {
  unauthorized: 'Your session has ended. Sign in again.',
  forbidden: 'You are not allowed to do that (owner verification may be required).',
  node_has_no_runtimes: 'This computer has not reported any AI tools yet. Wait a minute after it connects and try again.',
  invalid_input: 'That input was rejected. Check the fields and try again.',
  not_found: 'That item no longer exists.',
  conflict: 'This was already submitted with different content.',
  ambiguous_worker_target: 'Brunel could not tell which Worker should do this. Choose one of the Workers offered, or reply in Command with “Worker N should do this.”',
  no_eligible_worker: 'No Worker here is set up with the AI or computer you named. Reply in Command with another Worker or AI.',
  ambiguous_runtime_target: 'Brunel could not tell which AI should do this. Choose one of those offered, or reply in Command with the one AI to use.',
  worker_target_conflict: 'The command names a different Worker from the one this plan picked. Send it again naming the Worker you want.',
  destination_incomplete: 'The repository or workspace this plan targets is no longer set up for that project. Fix the project setup, then approve again.',
  worker_retired: 'That Worker has been retired. Choose another Worker.',
  worker_not_ready: 'That Worker has no open session in this project, so it cannot take this. Choose another Worker.',
  runtime_not_on_machine: 'That Worker’s computer does not have the AI this names. Choose another Worker, or reply in Command with another AI.',
  machine_mismatch: 'That Worker is not on the computer this names. Choose another Worker.',
  rate_limited: 'Too many requests. Wait a moment.',
  internal: 'The request did not complete. Try again.',
};

export function errorMessage(code: string): string {
  return MESSAGES[code] ?? MESSAGES.internal!;
}

/** Submit a guarded input; returns the typed result and tracks busy/error state. */
export function useGuardedMutation(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useCallback(
    async <N extends BrowserRpcName>(name: N, request: GuardedRpcRequest<N>, context?: RpcTransportContext): Promise<RpcResult<GuardedRpcResult<N>>> => {
      setBusy(true);
      setError(null);
      try {
        const result = await sendWithRetry(transportFor(client), prepareMutation(name, request, context));
        if (!result.ok) setError(errorMessage(result.error.code));
        return result;
      } finally {
        setBusy(false);
      }
    },
    [client],
  );
  return { submit, busy, error };
}

async function ownerRpc(
  client: SupabaseClient,
  name: string,
  request: Record<string, unknown>,
): Promise<{ ok: boolean; error?: string; value?: Record<string, unknown> }> {
  const { data, error: transportError } = await client.rpc(name, { request });
  const result = data as { ok?: boolean; error?: { code?: string }; value?: Record<string, unknown> } | null;
  if (transportError || !result?.ok) {
    const code = result?.error?.code ?? 'internal';
    return { ok: false, error: errorMessage(code) };
  }
  return { ok: true, value: result.value };
}

/** Owner step-up: set the canonical approval policy, including autonomous mode. */
export function useApprovalPolicyMutation(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useCallback(async (orgId: string, mode: string): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const result = await ownerRpc(client, 'set_approval_policy', { orgId, mode });
      if (!result.ok) { setError(result.error ?? 'internal'); return false; }
      return true;
    } finally { setBusy(false); }
  }, [client]);
  /** #805: plan approval on ('ask') or off ('auto'); the tool-approval mode is untouched. */
  const savePlanApproval = useCallback(async (orgId: string, planApproval: 'auto' | 'ask'): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const result = await ownerRpc(client, 'set_approval_policy', { orgId, planApproval });
      if (!result.ok) { setError(result.error ?? 'internal'); return false; }
      return true;
    } finally { setBusy(false); }
  }, [client]);
  return { save, savePlanApproval, busy, error };
}

/** Create a one-time enrollment code for a new node (owner, verified session). */
export function useNodeEnrollment(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCallback(async (orgId: string, nodeName: string): Promise<{ code: string; expiresAt: string } | null> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('create_node_enrollment', { request: { orgId, nodeName } });
      const result = data as { ok?: boolean; value?: { code: string; expiresAt: string }; error?: { code?: string } } | null;
      if (transportError || !result?.ok || !result.value) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return null;
      }
      return result.value;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { create, busy, error };
}

/**
 * Put a node to work on a project, or take it off (owner, verified session).
 * `seats` is how many workers that computer may hold at once; leaving it out keeps what it has.
 */
export function useNodeProject(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setProject = useCallback(async (orgId: string, nodeId: string, projectId: string, enabled: boolean, folder?: string | null, seats?: number): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      // Only the keys the owner actually set are sent; the guarded RPC accepts each shape exactly.
      const request = {
        orgId, nodeId, projectId, enabled,
        ...(folder === undefined ? {} : { folder }),
        ...(seats === undefined ? {} : { seats }),
      };
      const { data, error: transportError } = await client.rpc('set_node_project', { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { setProject, busy, error };
}

/**
 * Say how hard a computer may be worked (owner, verified session).
 *
 * Only the limits the owner actually changed are sent - seats, either reserve, either
 * sustained ceiling - or the name of one preset, which the guarded write turns into its
 * own exact numbers. A limit the database refuses comes straight back to the owner rather
 * than being swallowed here.
 */
export function useNodeLimits(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setLimits = useCallback(async (
    orgId: string, nodeId: string, fields: LimitsFields | CapacityLimitsFields | { readonly profile: CapacityPresetKey },
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const request = { orgId, nodeId, ...fields };
      const { data, error: transportError } = await client.rpc('set_node_limits', { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { setLimits, busy, error };
}

/** The Workers a refused approval says can answer it; anything malformed is dropped, never guessed. */
export function workerChoicesFrom(value: unknown): WorkerChoice[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return [];
    const row = item as Record<string, unknown>;
    if (typeof row.workerNumber !== 'number' || !Number.isInteger(row.workerNumber) || row.workerNumber <= 0) return [];
    const text = (key: string) => (typeof row[key] === 'string' ? { [key]: row[key] as string } : {});
    return [{ workerNumber: row.workerNumber, ...text('name'), ...text('machine'), ...text('runtime') }];
  });
}

/**
 * Approve or reject a proposed plan (owner, verified session). Approval creates the mission and its tasks.
 * An approval can carry the Worker the owner chose; a refused approval that a Worker choice can
 * answer returns those Workers as `choices`, so the owner is given a control, not a dead end.
 */
export function usePlanDecision(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<WorkerChoice[]>([]);
  const decide = useCallback(async (orgId: string, planId: string, decision: 'approve' | 'reject' | 'retry', workerNumber?: number): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const request = workerNumber === undefined ? { orgId, planId, decision } : { orgId, planId, decision, workerNumber };
      const { data, error: transportError } = await client.rpc('decide_plan', { request });
      const result = data as { ok?: boolean; error?: { code?: string; choices?: unknown } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        setChoices(workerChoicesFrom(result?.error?.choices));
        return false;
      }
      setChoices([]);
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { decide, busy, error, choices };
}

/** Stop a run that is going nowhere (owner, verified session). Frees the worker for the next task. */
export function useStopRun(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRefusal = useRef<string | null>(null);
  const stop = useCallback(async (orgId: string, assignmentId: string, reason?: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const trimmed = (reason ?? '').trim();
      const request = trimmed ? { orgId, assignmentId, reason: trimmed } : { orgId, assignmentId };
      const { data, error: transportError } = await client.rpc('stop_run', { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        const message = errorMessage(result?.error?.code ?? 'internal');
        lastRefusal.current = message;
        setError(message);
        return false;
      }
      lastRefusal.current = null;
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { stop, busy, error, lastRefusal };
}


/** Publish an immutable node bundle, target one computer, or turn automatic idle updates on or off (owner, verified session). */
export function useNodeRelease(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(async (name: 'publish_node_release' | 'set_node_version' | 'set_node_update_settings', request: Record<string, unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc(name, { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);

  const publish = useCallback(async (request: {
    orgId: string; version: string; bundleUrl: string; bundleSha256: string; notes?: string;
  }): Promise<boolean> => call('publish_node_release', request), [call]);

  const target = useCallback(async (orgId: string, nodeId: string, version: string | null): Promise<boolean> =>
    call('set_node_version', { orgId, nodeId, version }), [call]);

  const setAutomaticWhenIdle = useCallback(async (orgId: string, automaticWhenIdle: boolean): Promise<boolean> =>
    call('set_node_update_settings', { orgId, automaticWhenIdle }), [call]);

  return { publish, target, setAutomaticWhenIdle, busy, error };
}


export interface LaunchSpecRequest {
  orgId: string;
  runtimeKey: string;
  file: string;
  args: readonly string[];
  needsModel: boolean;
  position: number;
  env?: Readonly<Record<string, string>>;
}

/** Owner-controlled M34 launch policy: one runtime spec at a time, centrally. */
export function useLaunchCatalog(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(async (name: 'set_launch_spec' | 'clear_launch_spec', request: Record<string, unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc(name, { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);

  const setSpec = useCallback(async (request: LaunchSpecRequest): Promise<boolean> =>
    call('set_launch_spec', {
      orgId: request.orgId, runtimeKey: request.runtimeKey, file: request.file,
      args: [...request.args], needsModel: request.needsModel, position: request.position,
      ...(request.env === undefined ? {} : { env: { ...request.env } }),
    }), [call]);

  const clearSpec = useCallback(async (orgId: string, runtimeKey: string): Promise<boolean> =>
    call('clear_launch_spec', { orgId, runtimeKey }), [call]);

  return { setSpec, clearSpec, busy, error };
}


export type NodeInstructionKind = 'pause' | 'resume' | 'reconnect' | 'restart' | 'provision' | 'rescan_runtimes';

/** Ask the execution host to open a bound worktree. Never sends a filesystem path. */
export function useOpenWorkspace(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = useCallback(async (
    orgId: string, nodeId: string, projectId: string, adapterKey: string,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('request_open_workspace', {
        request: { orgId, nodeId, projectId, adapterKey },
      });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { open, busy, error };
}

/** Tell one computer to perform one closed self-action. No command text can enter this lane. */
export function useNodeInstruction(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const instruct = useCallback(async (orgId: string, nodeId: string, instruction: NodeInstructionKind): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('instruct_node', { request: { orgId, nodeId, instruction } });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { instruct, busy, error };
}


/** Pin one idle Worker to an AI offering on its node, or null to return it to Auto. */
export function useWorkerOffering(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setOffering = useCallback(async (
    orgId: string, workerId: string, nodeId: string, offeringKey: string | null,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('set_worker_offering', {
        request: { orgId, workerId, nodeId, offeringKey },
      });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        const code = result?.error?.code ?? 'internal';
        setError(code === 'conflict'
          ? 'That Worker is busy. Finish or stop its current run before switching AI.'
          : errorMessage(code));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { setOffering, busy, error };
}


/**
 * The standing routing instruction, set or lifted by the owner's own hand.
 *
 * A number pins every new task in that project to one Worker; null is Auto, which is what
 * Brunel has always done when nobody said otherwise. The database decides whether the number
 * is a Worker this organization has - the screen only asks.
 */
export function useRoutingScope(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = useCallback(async (orgId: string, projectId: string, workerNumber: number | null): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('set_worker_routing_scope', {
        request: { orgId, projectId, workerNumber },
      });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { set, busy, error };
}

/** Say something to a Worker session without relaying through another AI. Delivered at the next run/step boundary. */
export function useWorkerCorrection(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const correct = useCallback(async (orgId: string, projectId: string, sessionId: string, body: string): Promise<boolean> => {
    const text = body.trim();
    if (text === '') { setError('Write a correction first.'); return false; }
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('correct_worker', {
        request: { orgId, projectId, sessionId, body: text },
      });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { correct, busy, error };
}


/**
 * Owner terminal controls.
 *
 * Every verb goes through the guarded call of the same name, which is where the policy,
 * the input hold and the record of what was decided all live. Nothing here decides
 * anything: it names the source so the database can.
 */
export type TerminalSource = 'owner_terminal' | 'owner_composer' | 'command_ai' | 'worker_automation' | 'system';

export function useTerminalControl(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRefusal = useRef<string | null>(null);

  const call = useCallback(async (
    name: 'open_terminal' | 'write_terminal' | 'resize_terminal' | 'close_terminal'
      | 'claim_terminal_input' | 'release_terminal_input' | 'register_controller'
      | 'request_command_plan' | 'withdraw_command_plan' | 'record_plan_sent' | 'submit_plan_observation',
    request: Record<string, unknown>,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc(name, { request });
      const result = data as { ok?: boolean; value?: Record<string, unknown>; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        const message = errorMessage(result?.error?.code ?? 'internal');
        lastRefusal.current = message;
        setError(message);
        return null;
      }
      lastRefusal.current = null;
      return result.value ?? {};
    } finally {
      setBusy(false);
    }
  }, [client]);

  const open = useCallback(async (request: {
    orgId: string; nodeId: string; label: string; shell: string | null; cwd: string | null;
    columns: number; rows: number; workerId?: string | null;
  }): Promise<string | null> => {
    const value = await call('open_terminal', request.workerId === undefined ? request : { ...request, workerId: request.workerId });
    return typeof value?.['sessionId'] === 'string' ? value['sessionId'] : null;
  }, [call]);

  const write = useCallback(async (
    orgId: string, sessionId: string, data: string,
    source: TerminalSource = 'owner_terminal', controllerId?: string, lineOpen?: boolean,
  ): Promise<boolean> =>
    (await call('write_terminal', {
      orgId, sessionId, data, source,
      ...(controllerId === undefined ? {} : { controllerId }),
      ...(lineOpen === undefined ? {} : { lineOpen }),
    })) !== null, [call]);

  const resize = useCallback(async (orgId: string, sessionId: string, columns: number, rows: number, source: TerminalSource = 'owner_terminal'): Promise<boolean> =>
    (await call('resize_terminal', { orgId, sessionId, columns, rows, source })) !== null, [call]);

  const close = useCallback(async (orgId: string, sessionId: string, source: TerminalSource = 'owner_terminal'): Promise<boolean> =>
    (await call('close_terminal', { orgId, sessionId, source })) !== null, [call]);

  /** Lend the keyboard to one named controller for a while. */
  const claim = useCallback(async (
    orgId: string, sessionId: string, source: TerminalSource, seconds: number, controllerId?: string,
  ): Promise<boolean> =>
    (await call('claim_terminal_input', controllerId === undefined
      ? { orgId, sessionId, source, seconds }
      : { orgId, sessionId, source, seconds, controllerId })) !== null, [call]);

  /** Give the keyboard back. A controller releases what it holds; the owner takes it from anything. */
  const release = useCallback(async (
    orgId: string, sessionId: string, controllerId: string | null, reason?: string,
  ): Promise<boolean> =>
    (await call('release_terminal_input', reason === undefined
      ? { orgId, sessionId, controllerId }
      : { orgId, sessionId, controllerId, reason })) !== null, [call]);

  /** Name a controller, or get back the one of that name. */
  const registerController = useCallback(async (
    orgId: string, name: string, kind: 'command_ai' | 'worker_automation',
  ): Promise<string | null> => {
    const value = await call('register_controller', { orgId, name, kind });
    return typeof value?.['controllerId'] === 'string' ? value['controllerId'] : null;
  }, [call]);

  /** The owner states a goal for one Worker, or approves an instruction himself. */
  const requestPlan = useCallback(async (
    orgId: string, workerId: string, goal: string,
    approved?: { instruction: string; previousPlanId: string },
  ): Promise<string | null> => {
    const value = await call('request_command_plan', approved === undefined
      ? { orgId, workerId, goal }
      : { orgId, workerId, goal, instruction: approved.instruction, previousPlanId: approved.previousPlanId });
    return typeof value?.['planId'] === 'string' ? value['planId'] : null;
  }, [call]);

  const withdrawPlan = useCallback(async (orgId: string, planId: string): Promise<boolean> =>
    (await call('withdraw_command_plan', { orgId, planId })) !== null, [call]);

  const recordPlanSent = useCallback(async (orgId: string, planId: string, controllerId: string): Promise<boolean> =>
    (await call('record_plan_sent', { orgId, planId, controllerId })) !== null, [call]);

  const submitObservation = useCallback(async (orgId: string, planId: string, observation: string): Promise<boolean> =>
    (await call('submit_plan_observation', { orgId, planId, observation })) !== null, [call]);

  return { open, write, resize, close, claim, release, registerController,
           requestPlan, withdrawPlan, recordPlanSent, submitObservation, busy, error, lastRefusal };
}


export interface CommandPlannerHostRequest {
  readonly orgId: string;
  readonly nodeId: string | null;
  readonly runtimeKey: string;
  readonly fallbackMode: 'none' | 'api';
  readonly enabled: boolean;
}

export type CommandPlannerHostSaveOutcome = 'saved' | 'rejected' | 'unknown';

const PLANNER_HOST_MESSAGES: Readonly<Record<string, string>> = {
  unauthorized: 'Your session has ended. Sign in again.',
  forbidden: 'You are not allowed to change the Planner Host (owner verification may be required).',
  invalid_input: 'That Planner Host selection was rejected. Check the fields and try again.',
  not_found: 'That computer or runtime no longer exists.',
  node_unreachable: 'That computer is offline or out of touch.',
  runtime_not_offered: 'That computer is not offering the selected runtime.',
  runtime_unavailable: 'That runtime is currently unavailable on the selected computer.',
};

const PLANNER_HOST_UNKNOWN =
  'Brunel could not confirm whether the Planner Host setting saved. The current server state will be reread; the write will not be retried automatically.';

/**
 * Planner Host configuration is a direct owner mutation, not one of M1's
 * receipt-backed guarded-input RPCs. Call it exactly once. If transport fails
 * after the server may have committed, return "unknown" so the screen rereads
 * authoritative server state instead of replaying a non-idempotent write.
 */
export interface CommandPlannerHostSaveResult {
  readonly outcome: CommandPlannerHostSaveOutcome;
  readonly message: string | null;
}

/**
 * Exactly one network attempt. A lost response is deliberately "unknown": the
 * caller must reread authoritative server state instead of replaying the write.
 */
export async function saveCommandPlannerHostOnce(
  client: SupabaseClient,
  request: CommandPlannerHostRequest,
): Promise<CommandPlannerHostSaveResult> {
  let data: unknown;
  try {
    const response = await client.rpc('set_command_planner_host', { request });
    if (response.error) return { outcome: 'unknown', message: PLANNER_HOST_UNKNOWN };
    data = response.data;
  } catch {
    return { outcome: 'unknown', message: PLANNER_HOST_UNKNOWN };
  }

  const result = data as { ok?: boolean; error?: { code?: string } } | null;
  if (result?.ok === true) return { outcome: 'saved', message: null };

  const code = result?.error?.code ?? 'internal';
  return {
    outcome: 'rejected',
    message: PLANNER_HOST_MESSAGES[code] ?? 'The Planner Host setting was rejected.',
  };
}

/** The Overseer heartbeat switch as the server answered it; "unknown" means reread, never replay. */
export type AutopilotHeartbeatSaveResult =
  | { readonly outcome: 'saved'; readonly paused: boolean; readonly roundRunning: boolean }
  | { readonly outcome: 'rejected'; readonly code: string }
  | { readonly outcome: 'unknown' };

/** Owner AAL2: pause or resume the 15-minute Overseer heartbeat. Exactly one network attempt. */
export async function setAutopilotHeartbeatOnce(
  client: SupabaseClient,
  request: { readonly orgId: string; readonly paused: boolean },
): Promise<AutopilotHeartbeatSaveResult> {
  let data: unknown;
  try {
    const response = await client.rpc('set_autopilot_heartbeat', { request });
    if (response.error) return { outcome: 'unknown' };
    data = response.data;
  } catch {
    return { outcome: 'unknown' };
  }
  const result = data as { ok?: boolean; value?: { paused?: boolean; roundRunning?: boolean }; error?: { code?: string } } | null;
  if (result?.ok === true) {
    return { outcome: 'saved', paused: result.value?.paused === true, roundRunning: result.value?.roundRunning === true };
  }
  return { outcome: 'rejected', code: result?.error?.code ?? 'internal' };
}

export function useCommandPlannerHost(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(async (request: CommandPlannerHostRequest): Promise<CommandPlannerHostSaveOutcome> => {
    setBusy(true);
    setError(null);
    try {
      const result = await saveCommandPlannerHostOnce(client, request);
      setError(result.message);
      return result.outcome;
    } finally {
      setBusy(false);
    }
  }, [client]);

  return { save, busy, error };
}

export interface UpsertRoadmapItemRequest {
  readonly orgId: string;
  readonly id?: string;
  readonly title: string;
  readonly summary?: string;
  readonly horizon: 'current' | 'next' | 'later';
  readonly status?: 'open' | 'in_progress' | 'blocked' | 'done' | 'dropped';
  readonly rank?: number;
  readonly projectId?: string | null;
  readonly ownerLabel?: string | null;
  readonly sourceCommandId?: string | null;
}

export interface DeleteRoadmapItemRequest {
  readonly orgId: string;
  readonly id: string;
}

/** Create or edit a roadmap item; deletes ask the owner for confirmation in the UI. */
export function useRoadmapMutation(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsert = useCallback(async (request: UpsertRoadmapItemRequest): Promise<string | null> => {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        orgId: request.orgId,
        title: request.title,
        summary: request.summary ?? '',
        horizon: request.horizon,
        status: request.status ?? 'open',
        rank: request.rank ?? 100,
        projectId: request.projectId ?? null,
        ownerLabel: request.ownerLabel ?? null,
        sourceCommandId: request.sourceCommandId ?? null,
      };
      if (request.id) payload.id = request.id;
      const { data, error: transportError } = await client.rpc('upsert_roadmap_item', { request: payload });
      const result = data as { ok?: boolean; data?: { id?: string }; error?: { code?: string } } | null;
      if (transportError || !result?.ok || !result.data?.id) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return null;
      }
      return result.data.id;
    } finally {
      setBusy(false);
    }
  }, [client]);

  const remove = useCallback(async (request: DeleteRoadmapItemRequest): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('delete_roadmap_item', { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [client]);

  return { upsert, remove, busy, error };
}

export type MainCommandArchiveResult = {
  id: string;
  archivedAt: string;
  throughCommandSeq: number | null;
  title: string | null;
  conversationId: string | null;
};

export type MainCommandArchiveOptions = {
  title?: string | null;
  digest?: Record<string, unknown> | null;
  lastActiveAt?: string | null;
  continuedFromId?: string | null;
};

/** Archive the current shared Main Command conversation and start fresh (#415/#450). */
export function useArchiveMainCommandChat(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const archive = useCallback(async (
    orgId: string,
    throughCommandSeq: number | null,
    options: MainCommandArchiveOptions = {},
  ): Promise<MainCommandArchiveResult | null> => {
    setBusy(true);
    setError(null);
    try {
      const request: Record<string, unknown> = { orgId, throughCommandSeq };
      if (options.title !== undefined) request.title = options.title;
      if (options.digest !== undefined) request.digest = options.digest;
      if (options.lastActiveAt !== undefined) request.lastActiveAt = options.lastActiveAt;
      if (options.continuedFromId !== undefined) request.continuedFromId = options.continuedFromId;
      const { data, error: transportError } = await client.rpc('archive_main_command_chat', {
        request,
      });
      const result = data as {
        ok?: boolean;
        value?: {
          id?: string;
          archivedAt?: string;
          throughCommandSeq?: number | null;
          title?: string | null;
          conversationId?: string | null;
        };
        error?: { code?: string };
      } | null;
      if (transportError || !result?.ok || !result.value?.id || !result.value.archivedAt) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return null;
      }
      return {
        id: result.value.id,
        archivedAt: result.value.archivedAt,
        throughCommandSeq: result.value.throughCommandSeq ?? null,
        title: result.value.title ?? null,
        conversationId: result.value.conversationId ?? null,
      };
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { archive, busy, error };
}

/** Start a new Main Command chat (archives current first) (#450). */
export function useStartMainCommandChat(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = useCallback(async (
    orgId: string,
    throughCommandSeq: number | null,
    options: MainCommandArchiveOptions = {},
  ): Promise<{ conversationId: string; archive: MainCommandArchiveResult } | null> => {
    setBusy(true);
    setError(null);
    try {
      const request: Record<string, unknown> = { orgId, throughCommandSeq };
      if (options.title !== undefined) request.title = options.title;
      if (options.digest !== undefined) request.digest = options.digest;
      const { data, error: transportError } = await client.rpc('start_main_command_chat', { request });
      const result = data as {
        ok?: boolean;
        value?: {
          conversationId?: string;
          archive?: {
            id?: string;
            archivedAt?: string;
            throughCommandSeq?: number | null;
            title?: string | null;
            conversationId?: string | null;
          };
        };
        error?: { code?: string };
      } | null;
      const archive = result?.value?.archive;
      if (
        transportError || !result?.ok || !result.value?.conversationId
        || !archive?.id || !archive.archivedAt
      ) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return null;
      }
      return {
        conversationId: result.value.conversationId,
        archive: {
          id: archive.id,
          archivedAt: archive.archivedAt,
          throughCommandSeq: archive.throughCommandSeq ?? null,
          title: archive.title ?? null,
          conversationId: archive.conversationId ?? result.value.conversationId,
        },
      };
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { start, busy, error };
}

/** Continue from an archived chat into a NEW current conversation (#450). */
export function useContinueMainCommandChat(client: SupabaseClient) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const continueFrom = useCallback(async (
    orgId: string,
    archiveId: string,
    throughCommandSeq: number | null,
  ): Promise<{
    conversationId: string;
    title: string | null;
    continuedFromArchiveId: string;
    seedDigest: Record<string, unknown> | null;
  } | null> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: transportError } = await client.rpc('continue_main_command_chat', {
        request: { orgId, archiveId, throughCommandSeq },
      });
      const result = data as {
        ok?: boolean;
        value?: {
          conversationId?: string;
          title?: string | null;
          continuedFromArchiveId?: string;
          seedDigest?: Record<string, unknown> | null;
        };
        error?: { code?: string };
      } | null;
      if (
        transportError || !result?.ok || !result.value?.conversationId
        || !result.value.continuedFromArchiveId
      ) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return null;
      }
      return {
        conversationId: result.value.conversationId,
        title: result.value.title ?? null,
        continuedFromArchiveId: result.value.continuedFromArchiveId,
        seedDigest: result.value.seedDigest ?? null,
      };
    } finally {
      setBusy(false);
    }
  }, [client]);
  return { continueFrom, busy, error };
}
